import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  TablePagination,
  Slider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  VisibilityOff,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Straighten as StraightenIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  RemoveRedEye as MonitorIcon
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { SeguimientoContext } from '../SeguimientoContext';
import BigBoardFilters from '../components/BigBoardFilters';
import FlagDisplay from '../components/FlagDisplay';

/**
 * Calcule la couleur de texte adaptée au fond `hexcolor`.
 */
const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/* Helper components */
const FormaIndicator = ({ forma }) => {
  if (!forma) return null;

  switch (forma?.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    default:
      return null;
  }
};

const calculateDecimalAge = (anneeNaissance) => {
  if (!anneeNaissance) return '';
  const currentYear = new Date().getFullYear();
  const age = currentYear - anneeNaissance;
  return age;
};

const VerdictIndicator = ({ player }) => {
  if (!player.scout_verdicts || player.scout_verdicts.length === 0) return null;

  // Group verdicts by type
  const verdictGroups = player.scout_verdicts.reduce((acc, verdict) => {
    if (!acc[verdict.verdict]) {
      acc[verdict.verdict] = [];
    }
    acc[verdict.verdict].push(verdict);
    return acc;
  }, {});

  const verdictConfig = {
    'VOLVER A VER': { emoji: '🔄', color: '#2196f3' },
    'DESCARTADO': { emoji: '❌', color: '#f44336' },
    'MONITORIZAR': { emoji: '👁️', color: '#ff9800' },
    'PRIORITARIO': { emoji: '⭐', color: '#4caf50' },
    'DUDAS': { emoji: '❓', color: '#9c27b0' }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {Object.entries(verdictGroups).map(([verdictType, verdicts]) => {
        const config = verdictConfig[verdictType];
        if (!config) return null;

        const count = verdicts.length;

        return (
          <Box key={verdictType} sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s'
                }
              }}
            >
              {config.emoji}
            </Box>
            {count > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: config.color,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 'bold',
                  border: '1px solid #fff',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const CountBadge = ({ count }) => {
  const backgroundColor = count <= 1 ? '#f44336' : '#4caf50';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        color: '#ffffff',
        width: '28px',
        height: '28px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        border: '2px solid #fff',
      }}
    >
      {count}
    </Box>
  );
};

const badgeStyle = (bgColor) => ({
  position: 'relative',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '14px',
  marginLeft: '-4px',
  border: '1px solid #fff',
});

// Mappage des positions
const positionMappings = {
  Base: ['Base', 'Point Guard'],
  Escolta: ['Escolta', 'Shooting Guard', 'Guard'],
  Alerio: ['Alerio', 'Small Forward', 'Swingman'],
  'Ala-Pivot': ['Ala-Pivot', 'Ala-Pívot', 'Power Forward', 'Power Forward / Center'],
  Pivot: ['Pivot', 'Center'],
};

/**
 * Ligne de joueur dans le tableau de gauche (disponibles).
 * On peut glisser-déposer (drag) et cocher/décocher pour ajouter/retirer du suivi.
 */
const DraggablePlayerRow = ({
  player,
  handlePlayerClick,
  seguimientoPlayers,
  setSeguimientoPlayers,
  availablePlayers,
  setAvailablePlayers,
  mediaUrl
}) => {
  // Configuration pour le drag
  const [{ isDragging }, dragRef] = useDrag({
    type: 'player',
    item: player,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Vérifie si le joueur est déjà dans le suivi
  const isInSeguimiento = seguimientoPlayers.some((p) => p.id === player.id);

  /**
   * Ajoute le joueur au suivi (à droite).
   */
  const movePlayerToSeguimiento = (playerToMove) => {
    if (!isInSeguimiento) {
      const updatedSeguimiento = [...seguimientoPlayers, playerToMove].sort((a, b) =>
        a.nom.localeCompare(b.nom)
      );
      setSeguimientoPlayers(updatedSeguimiento);
      setAvailablePlayers((prev) => prev.filter((p) => p.id !== playerToMove.id));
    }
  };

  /**
   * Retire le joueur du suivi et le remet dans la liste de gauche.
   */
  const removePlayerFromSeguimiento = (playerToRemove) => {
    const updatedSeguimiento = seguimientoPlayers.filter((p) => p.id !== playerToRemove.id);
    setSeguimientoPlayers(updatedSeguimiento);
    setAvailablePlayers((prev) => [...prev, playerToRemove].sort((a, b) => a.nom.localeCompare(b.nom)));
  };

  /**
   * Gère le (dé)cochage de la case.
   */
  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      // Si on coche => on ajoute directement au suivi
      movePlayerToSeguimiento(player);
    } else {
      // Si on décoche => on le retire du suivi
      removePlayerFromSeguimiento(player);
    }
  };

  return (
    <TableRow
      ref={dragRef}
      key={player.id}
      hover
      sx={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
    >
      <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
        <Checkbox checked={isInSeguimiento} onChange={handleCheckboxChange} />
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagDisplay nationality={player.nationalite} />
          <Avatar
            src={`${mediaUrl}/photos/${player.id}.png`}
            alt={player.nom}
            sx={{ width: 36, height: 36 }}
          />
          <VerdictIndicator player={player} />
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="a"
            onClick={(e) => {
              e.preventDefault();
              handlePlayerClick(player);
            }}
            sx={{
              color: 'black',
              textDecoration: 'none',
              fontSize: '14px',
              '&:hover': {
                textDecoration: 'underline',
                cursor: 'pointer'
              }
            }}
          >
            {player.nom}
          </Box>
          {player.is_in_injury === 1 && (
            <HealingIcon sx={{ color: 'red', fontSize: 16 }} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        <FormaIndicator forma={player.forme_indicator} />
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.poste}
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.annee_naissance}
        {player.annee_naissance && ` (${calculateDecimalAge(player.annee_naissance)})`}
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.taille}cm
      </TableCell>
      <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
        <Box>
          <div>{player.nationalite && player.nationalite !== 'null' ? player.nationalite : ''}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {player.passport && player.passport !== 'null' ? player.passport : ''}
          </div>
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
        <Box>
          <div>{player.team}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{player.league_1}</div>
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        {player.groupes && player.groupes.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {player.groupes.slice(0, 2).map((group, idx) => (
              <Chip
                key={idx}
                label={group.libelle}
                size="small"
                sx={{
                  backgroundColor: group.code_couleur || '#grey',
                  color: getContrastColor(group.code_couleur),
                  fontSize: '11px',
                  height: '20px'
                }}
              />
            ))}
            {player.groupes.length > 2 && (
              <Chip
                label={`+${player.groupes.length - 2}`}
                size="small"
                sx={{ fontSize: '11px', height: '20px' }}
              />
            )}
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <CountBadge count={player.nb_informe || 0} />
          <CountBadge count={player.nb_scout || 0} />
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
          {player.is_in_big_board === 1 && (
            <Box sx={badgeStyle('#1976d2')}>
              <StarIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_follow_list === 1 && (
            <Box sx={badgeStyle('#dc004e')}>
              <VisibilityIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_informe === 1 && (
            <Box sx={badgeStyle('#4caf50')}>
              <AssignmentIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_intel === 1 && (
            <Box sx={badgeStyle('#ff9800')}>
              <LightbulbIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_fiche_technique === 1 && (
            <Box sx={badgeStyle('#2196f3')}>
              <DescriptionIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_video === 1 && (
            <Box sx={badgeStyle('#9c27b0')}>
              <VideoLibraryIcon fontSize="inherit" />
            </Box>
          )}
          {player.has_medidas === 1 && (
            <Box sx={badgeStyle('#00bcd4')}>
              <StraightenIcon fontSize="inherit" />
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/**
 * Composant principal pour la modification d'un Seguimiento.
 * Avec fusion du filtre (nom/équipe) et ajout d'un multiselect groupes.
 */
const SeguimientoModif = () => {
  const { tags = [] } = useContext(SeguimientoContext) || {};

  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;
  const userData = JSON.parse(localStorage.getItem('user'));
  const userRole = userData?.role?.toLowerCase() || '';

  // États divers
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [seguimientoName, setSeguimientoName] = useState('');
  const [seguimientoType, setSeguimientoType] = useState(''); // "pro" ou "cantera"

  const [selectedTags, setSelectedTags] = useState([]);
  const [seguimientoPlayers, setSeguimientoPlayers] = useState([]); // Joueurs à droite
  const [availablePlayers, setAvailablePlayers] = useState([]);     // Joueurs à gauche
  const [allPlayers, setAllPlayers] = useState([]);

  // -- Nouveaux états pour gérer les groupes --
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // -- Filtres fusionnés : un seul champ pour nom+équipe --
  const [searchFilter, setSearchFilter] = useState('');

  // -- Autres Filtres --
  const [positionFilter, setPositionFilter] = useState([]);
  const [heightFilter, setHeightFilter] = useState([100, 230]);
  const [ageFilter, setAgeFilter] = useState([8, 42]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [uniqueLeagues, setUniqueLeagues] = useState([]);

  // New filter states from BigBoard
  const [nameFilter, setNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [playerTypeFilter, setPlayerTypeFilter] = useState(0);
  const [informeFilter, setInformeFilter] = useState(null);
  const [scoutFilter, setScoutFilter] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState([]);
  const [groupColors, setGroupColors] = useState({});

  /**
   * Calcule l'âge à partir de l'année de naissance.
   */
  const calculateAge = (anneeNaissance) => {
    const currentYear = new Date().getFullYear();
    return currentYear - anneeNaissance;
  };

  /**
   * Charge le seguimiento depuis le backend, récupère son type,
   * charge les joueurs, puis filtre par type.
   */
  useEffect(() => {
    const fetchSeguimiento = async () => {
      try {
        setLoading(true);

        // 1) Récupération du seguimiento
        const response = await api.get(`/api/seguimiento/${id}`);
        const seguimiento = response.data;

        setSeguimientoName(seguimiento.libelle || '');
        setSelectedTags(seguimiento.tags || []);

        // On récupère le type => "Pro" / "Cantera" ou minuscules
        const segType = seguimiento.type ? seguimiento.type.toLowerCase() : '';
        setSeguimientoType(segType);

        // Joueurs du seguimiento => triés
        const sortedPlayers = seguimiento.players
          ? [...seguimiento.players].sort((a, b) => a.nom.localeCompare(b.nom))
          : [];
        setSeguimientoPlayers(sortedPlayers);

        // 2) Récupération de tous les joueurs
        const playersResponse = await api.get('/api/players');
        const allPlayersFetched = playersResponse.data.players || [];

        // 3) Filtrer les joueurs par type (player.type == "pro" ou "cantera")
        const filteredByType = allPlayersFetched.filter((p) => {
          if (!p.type) return false;
          return p.type.toLowerCase() === segType;
        });

        setAllPlayers(filteredByType);
        setError(null);
      } catch (err) {
        console.error('Error al recuperar el Seguimiento:', err);
        setError('No se puede cargar el Seguimiento.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSeguimiento();
    }
  }, [id, backendUrl, section]);

  /**
   * Récupération de la liste des groupes (pour le multiselect).
   */
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('/api/groups');
        setGroups(response.data); // tableau d'objets { id, libelle, code_couleur, ... }

        // Create color mapping for groups
        const colors = {};
        response.data.forEach(group => {
          colors[group.libelle] = group.code_couleur;
        });
        setGroupColors(colors);
      } catch (error) {
        console.error('Erreur lors de la récupération des groupes:', error);
      }
    };
    fetchGroups();
  }, [backendUrl, section]);

  /**
   * Met à jour la liste des joueurs disponibles + ligues
   * à chaque fois que `seguimientoPlayers` ou `allPlayers` change.
   */
  useEffect(() => {
    const seguimientoPlayerIds = seguimientoPlayers.map((p) => p.id);
    const available = allPlayers.filter((p) => !seguimientoPlayerIds.includes(p.id));

    const leagues = Array.from(
      new Set(
        available
          .map((player) => player.league_1)
          .filter((league) => league && league.trim() !== '' && league.trim() !== 'null')
      )
    ).sort();

    setUniqueLeagues(leagues);
    setAvailablePlayers(available);
  }, [seguimientoPlayers, allPlayers]);

  /**
   * Filtre les joueurs disponibles en fonction des filtres.
   */
  const filteredAvailablePlayers = availablePlayers.filter((player) => {
    const playerAge = calculateAge(player.annee_naissance);

    // Name/team search (use nameFilter or fallback to searchFilter)
    const term = (nameFilter || searchFilter).toLowerCase();
    const matchesSearch =
      player.nom.toLowerCase().includes(term) ||
      (player.team && player.team.toLowerCase().includes(term));

    if (!matchesSearch) return false;

    // Year filter
    if (yearFilter && !player.annee_naissance.toString().includes(yearFilter))
      return false;

    // Player type filter
    if (playerTypeFilter === 1) {
      if (!player.type || player.type.toLowerCase() !== 'cantera') return false;
    } else if (playerTypeFilter === 2) {
      if (!player.type || player.type.toLowerCase() !== 'pro') return false;
    }

    // Informe filter
    if (informeFilter) {
      if (informeFilter === 'zero') {
        // 0 informes: players with exactly 0 informe
        const count = player.nb_informe || 0;
        if (count !== 0) return false;
      } else if (informeFilter === 'red') {
        // 0-1 informes: players with 0 or 1 informe
        const count = player.nb_informe || 0;
        if (count > 1) return false;
      } else if (informeFilter === 'green') {
        // 2+ informes: players with 2 or more informes
        const count = player.nb_informe || 0;
        if (count <= 1) return false;
      }
    }

    // Scout filter
    if (scoutFilter) {
      if (scoutFilter === 'red') {
        // 0-1 scouts: players with 0 or 1 scout
        const count = player.nb_scout || 0;
        if (count > 1) return false;
      } else if (scoutFilter === 'green') {
        // 2+ scouts: players with 2 or more scouts
        const count = player.nb_scout || 0;
        if (count <= 1) return false;
      }
    }

    // Verdict filter
    if (verdictFilter.length > 0) {
      // Check if player has any of the selected verdicts
      if (!player.scout_verdicts || player.scout_verdicts.length === 0) {
        return false;
      }

      // Check if any of the player's verdicts match the selected filters
      const playerVerdicts = player.scout_verdicts.map(v => v.verdict);
      const hasMatchingVerdict = verdictFilter.some(v => playerVerdicts.includes(v));

      if (!hasMatchingVerdict) return false;
    }

    // Position filter
    const matchesPosition =
      positionFilter.length === 0 ||
      positionFilter.some((pos) =>
        positionMappings[pos].some(
          (mappedPos) => player.poste?.toLowerCase() === mappedPos.toLowerCase()
        )
      );

    if (!matchesPosition) return false;

    // Group filter
    if (groupFilter.length > 0) {
      if (groupFilter.includes('Sin Grupos')) {
        if (!player.groupes || player.groupes.length === 0) {
          // Player has no groups - matches "Sin Grupos"
        } else if (!groupFilter.some(g => g !== 'Sin Grupos')) {
          return false;
        }
      } else {
        const playerGroupNames = (player.groupes || []).map((g) => g.libelle);
        const hasOverlap = groupFilter.some((name) => playerGroupNames.includes(name));
        if (!hasOverlap) return false;
      }
    }

    // Height filter
    const matchesHeight = player.taille >= heightFilter[0] && player.taille <= heightFilter[1];
    if (!matchesHeight) return false;

    // Age filter
    const matchesAge = playerAge >= ageFilter[0] && playerAge <= ageFilter[1];
    if (!matchesAge) return false;

    return true;
  });

  /**
   * Sauvegarde le suivi (PUT).
   */
  const handleSave = async () => {
    if (!seguimientoName.trim()) {
      setError('El nombre del seguimiento no puede estar vacío');
      return;
    }

    try {
      await api.put(`/api/seguimiento/${id}`, {
        libelle: seguimientoName,
        players: seguimientoPlayers.map((p) => p.id),
        tags: selectedTags.map((tag) => tag.id),
        type: seguimientoType === 'pro' ? 'Pro' : 'Cantera',
      });

      // Retour à la page précédente
      navigate(-1);
    } catch (err) {
      console.error('Error al actualizar el Seguimiento:', err);
      setError(err.response?.data?.message || 'Error al actualizar el Seguimiento.');
    }
  };

  /**
   * Clique sur le nom d'un joueur => navigue vers sa fiche.
   */
  const handlePlayerClick = (player) => {
    navigate(`/players/${player.id}`);
  };

  /**
   * Retire un joueur du suivi, côté dropzone (colonne de droite).
   */
  const removePlayerFromSeguimiento = (player) => {
    const updated = seguimientoPlayers.filter((p) => p.id !== player.id);
    setSeguimientoPlayers(updated);
    setAvailablePlayers((prev) => [...prev, player].sort((a, b) => a.nom.localeCompare(b.nom)));
  };

  /**
   * Ajoute un joueur dans le suivi si pas déjà présent (lors d'un drop).
   */
  const movePlayerToSeguimiento = (player) => {
    if (!seguimientoPlayers.some((p) => p.id === player.id)) {
      setSeguimientoPlayers((prev) => [...prev, player].sort((a, b) => a.nom.localeCompare(b.nom)));
      setAvailablePlayers((prev) => prev.filter((p) => p.id !== player.id));
    }
  };

  /**
   * Composant pour afficher un joueur déjà dans le suivi (colonne de droite).
   */
  const SeguimientoPlayer = ({ player }) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 0.5,
          mb: 0.5,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Avatar
          src={`${mediaUrl}/photos/${player.id}.png`}
          alt={player.nom}
          sx={{ mr: 2 }}
        />
        <Typography sx={{ flexGrow: 1 }}>{player.nom}</Typography>
        <IconButton onClick={() => removePlayerFromSeguimiento(player)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  /**
   * Zone de dépôt (drag and drop) pour la liste de suivi (colonne de droite).
   */
  const SeguimientoDropZone = () => {
    const [{ isOver }, dropRef] = useDrop({
      accept: 'player',
      drop: (player) => movePlayerToSeguimiento(player),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    });

    return (
      <Paper
        ref={dropRef}
        sx={{
          p: 2,
          minHeight: '500px',
          backgroundColor: isOver ? 'rgba(255, 152, 0, 0.1)' : '#f5f5f5',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Seguimiento
        </Typography>
        {seguimientoPlayers.map((player) => (
          <SeguimientoPlayer key={player.id} player={player} />
        ))}
        {seguimientoPlayers.length === 0 && (
          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mt: 2,
            }}
          >
            Arrastra jugador@s aquí
          </Typography>
        )}
      </Paper>
    );
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Erreur
  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 3 }}>
        {/* Filtros */}
        <BigBoardFilters
          playerTypeFilter={playerTypeFilter}
          setPlayerTypeFilter={setPlayerTypeFilter}
          informeFilter={informeFilter}
          setInformeFilter={setInformeFilter}
          scoutFilter={scoutFilter}
          setScoutFilter={setScoutFilter}
          verdictFilter={verdictFilter}
          setVerdictFilter={setVerdictFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          yearFilter={yearFilter}
          setYearFilter={setYearFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          groupFilter={groupFilter}
          setGroupFilter={setGroupFilter}
          heightFilter={heightFilter}
          setHeightFilter={setHeightFilter}
          ageFilter={ageFilter}
          setAgeFilter={setAgeFilter}
          groups={groups}
          groupColors={groupColors}
          userRole={userRole}
        />

        {/* Configuración del Seguimiento */}
        <Box sx={{ mb: 6, mt: 6 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Nombre del Seguimiento"
                value={seguimientoName}
                onChange={(e) => setSeguimientoName(e.target.value)}
                error={!seguimientoName.trim()}
                helperText={!seguimientoName.trim() ? 'El nombre es obligatorio' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={selectedTags}
                  onChange={(e) => setSelectedTags(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.libelle}
                          style={{
                            backgroundColor: tag.code_couleur,
                            color: getContrastColor(tag.code_couleur),
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {tags.map((tag) => (
                    <MenuItem key={tag.id} value={tag}>
                      <Checkbox checked={selectedTags.some((t) => t.id === tag.id)} />
                      <ListItemText primary={tag.libelle} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCELAR
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              GUARDAR CAMBIOS
            </Button>
          </Box>
        </Box>

        {/* Tableau de gauche (joueurs disponibles) + Dropzone (droite) */}
        <Grid container spacing={3}>
          {/* Tableau des joueurs disponibles */}
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#F25F29',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '50px',
                        padding: '8px 4px'
                      }}
                    >
                      <Checkbox
                        disabled
                        sx={{ color: 'white' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'inline-block'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{filteredAvailablePlayers.length}</span>
                        <span style={{ fontSize: '9px', opacity: 0.8, marginLeft: '2px' }}>Jugador@s</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '120px', padding: '8px 4px' }}>
                      Nombre
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      Forma
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      Posición
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '100px', padding: '8px 4px' }}>
                      Año
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      Altura
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '100px', padding: '8px 4px' }}>
                      Nacionalidad
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      Equipo
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      Grupos
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                      Informe/Scout
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '140px', padding: '8px 4px' }}>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAvailablePlayers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((player) => (
                      <DraggablePlayerRow
                        key={player.id}
                        player={player}
                        handlePlayerClick={handlePlayerClick}
                        seguimientoPlayers={seguimientoPlayers}
                        setSeguimientoPlayers={setSeguimientoPlayers}
                        availablePlayers={availablePlayers}
                        setAvailablePlayers={setAvailablePlayers}
                        mediaUrl={mediaUrl}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAvailablePlayers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              rowsPerPageOptions={[10]}
            />
          </Grid>

          {/* Dropzone (joueurs déjà en suivi) */}
          <Grid item xs={12} md={4}>
            <SeguimientoDropZone />
          </Grid>
        </Grid>
      </Box>
    </DndProvider>
  );
};

export default SeguimientoModif;
